import type { Sense } from '#/protobuf/bareun/language_service_pb';

/**
 * WSD(동형이의어 의미 구분) 결과 표시용 순수 함수 모음.
 *
 * 서버가 tag-sense-model 을 로드하고 요청이 with_sense=true 일 때만 형태소에 sense 가
 * 붙는다. 조사·어미처럼 의미를 갖지 않는 형태소에는 원래 붙지 않으므로, 표시 로직은
 * "sense 가 없는 것이 정상"임을 전제로 한다.
 */

/** 말뭉치·sense 어휘의 어깨번호 표기는 3자리 0채움이다("001"). */
const SENSE_NO_PAD = 3;

/** 어깨번호를 원래 표기(3자리 0채움)로 되돌린다. 예: 5 → "005" */
export function formatSenseNo(senseNo: number): string {
  return String(senseNo).padStart(SENSE_NO_PAD, '0');
}

/**
 * API·문서에서 쓰는 표기. 예: ("다리", 5) → "다리__005"
 * MCP 의 formatCompact 및 매뉴얼 표기와 같은 형태다.
 */
export function senseApiNotation(text: string, senseNo: number): string {
  return `${text}__${formatSenseNo(senseNo)}`;
}

/**
 * 우리말샘 표제어 링크. urimal_target_id 는 0(미부여)일 수 있어 그때는 null 이다.
 * 링크 형식은 proto 주석에 명시된 것을 따른다.
 */
export function urimalDictUrl(urimalTargetId: number): string | null {
  if (!urimalTargetId) return null;
  return `https://opendict.korean.go.kr/dictionary/view?sense_no=${urimalTargetId}`;
}

/**
 * 뜻풀이. 빈 값일 수 있다 — 우리말샘 조인 매칭률이 100% 가 아니다(실측 99.4%).
 * sense 가 붙었다고 뜻풀이가 있다고 가정하면 빈 칸이 그대로 노출된다.
 */
export function senseMeaning(sense: Pick<Sense, 'meaning'>): string | null {
  const meaning = sense.meaning?.trim();
  return meaning ? meaning : null;
}

/**
 * 선택 확률을 백분율 문자열로. 후보 집합 안에서의 softmax 라 후보가 하나뿐이면
 * 항상 100 이다("확신"이 아니라 "고를 것이 하나뿐"이라는 뜻).
 *
 * ⚠️ Morpheme.probability(클래스별 독립 시그모이드)와 척도가 달라 같은 임계값으로
 * 비교하면 안 된다. 그래서 표에서도 두 값을 다른 자리에 둔다.
 */
export function formatSenseProbability(probability: number): string {
  return (probability * 100).toFixed(1).replace(/\.0$/, '');
}

/** 마우스 오버 요약: 뜻풀이(없으면 안내) + 어깨번호. */
export function senseTooltipText(
  text: string,
  sense: Pick<Sense, 'senseNo' | 'meaning'>
): string {
  const meaning = senseMeaning(sense) ?? '뜻풀이가 등록되지 않은 의미입니다.';
  return `${senseApiNotation(text, sense.senseNo)} · ${meaning}`;
}
